import React, { useState, useEffect } from 'react';
import { api } from './api';
import { ethers } from 'ethers';
import QSMPanel from './QSMPanel';
declare global { interface Window { ethereum?: any; } }

interface Market {
  symbol: string;
  min_leverage: number;
  max_leverage: number;
}

interface CleanTradingDashboardProps {
  walletAddress: string;
}

const CleanTradingDashboard: React.FC<CleanTradingDashboardProps> = ({ walletAddress }) => {
  const ESCROW_ADDRESS = "0xdF7c107a6d33E73e85b04f97A82634497C1E2Add";
  const USDC_ADDRESS = "0xaf88d065e77c8cC2239327C5EDb3A432268e5831";
  const [markets, setMarkets] = useState<Market[]>([]);
  const [pair, setPair] = useState('ETH/USDC');
  const [balance, setBalance] = useState(0);
  const [escrowBalance, setEscrowBalance] = useState(0);
  const [escrowStatus, setEscrowStatus] = useState("✅ Sufficient funds");

  const handleDepositMore = async () => {
    try {
      const depositAmount = parseFloat(prompt("Enter deposit amount (USDC):") || "0");
      if (depositAmount <= 0) return;
      
      if (!window.ethereum) {
        alert("Please install MetaMask to deposit funds!");
        return;
      }
      
      const provider = new ethers.BrowserProvider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = await provider.getSigner();
      
      const amountWei = ethers.parseUnits(depositAmount.toString(), 6);
      
      const usdcContract = new ethers.Contract(USDC_ADDRESS, ["function approve(address,uint256) returns(bool)"], signer);
      
      alert("Step 1: Approve USDC spending (check MetaMask)");
      const approveTx = await usdcContract.approve(ESCROW_ADDRESS, amountWei);
      await approveTx.wait();
      
      const escrowContract = new ethers.Contract(ESCROW_ADDRESS, ["function deposit(uint256)"], signer);
      
      alert("Step 2: Deposit to escrow (check MetaMask)");
      const depositTx = await escrowContract.deposit(amountWei);
      await depositTx.wait();
      
      alert("Deposited " + depositAmount + " USDC successfully!");
      await fetchEscrowBalance(); // Fetch real balance from blockchain
    } catch (error: any) {
      alert("Deposit failed: " + error.message);
    }
  };
  const handleViewHistory = async () => {
    try {
      const response = await fetch("/api/admin/weekly-history", {
        headers: { "X-User-ID": userId }
      });
      const data = await response.json();
      if (data.success && data.data.length > 0) {
        const history = data.data.map((item: any) => 
          `Week ${item.week_start} to ${item.week_end}: P&L $${item.total_pnl}, Share $${item.profit_share}`
        ).join("\n");
        alert("Weekly History:\n" + history);
      } else {
        alert("No weekly history found");
      }
    } catch (error) {
      alert("Failed to load history");
    }
  };
  const [leverage, setLeverage] = useState(1);
  const [tradePct, setTradePct] = useState(20);
  const [price, setPrice] = useState(0);
  const [websocketConnected, setWebsocketConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [minLeverage, setMinLeverage] = useState(1);
  const [maxLeverage, setMaxLeverage] = useState(100);
  const [isTrading, setIsTrading] = useState(false);

  const [position, setPosition] = useState<any>(null);
  const [pnl, setPnl] = useState<any>(null);
  const [weeklyPnl, setWeeklyPnl] = useState<any>(null);
  const [weeklyPnlLoading, setWeeklyPnlLoading] = useState(false);
  const [momentum, setMomentum] = useState<string>("Unknown");
  const [chopData, setChopData] = useState<any>(null);
  const [strategyType, setStrategyType] = useState("original");
  const [userId, setUserId] = useState<string>("");
  const [sessionLoaded, setSessionLoaded] = useState(false);
  // Poll WebSocket status - only when trading for accuracy
  useEffect(() => {
    const fetchWebSocketStatus = async () => {
      if (!isTrading) {
        setWebsocketConnected(false);
        setLastUpdated(new Date().toLocaleTimeString());
        return;
      }
      try {
        const response = await fetch("/api/trading/websocket-status", { headers: { "X-User-ID": userId } });
        const data = await response.json();
        setWebsocketConnected(data.websocket_connected);
        setLastUpdated(new Date().toLocaleTimeString());
      } catch (error) {
        setWebsocketConnected(false);
      }
    };
    fetchWebSocketStatus();
    const interval = setInterval(fetchWebSocketStatus, 30000);
    return () => clearInterval(interval);
  }, [isTrading]);

  // Fetch Weekly P&L when trading starts
  useEffect(() => {
    if (isTrading) {
      const fetchWeeklyPnl = async () => {
        try {
          setWeeklyPnlLoading(true);
          const response = await fetch('/api/trading/weekly-pnl/ALL', { headers: { "X-User-ID": userId } });
          if (response.ok) {
            const data = await response.json();
            setWeeklyPnl(data.data);
          }
        } catch (error) {
          console.error('Weekly P&L fetch failed:', error);
        } finally {
          setWeeklyPnlLoading(false);
        }
      };
      fetchWeeklyPnl();
    }
  }, [isTrading]);

  // Load markets
  useEffect(() => {
    const loadMarkets = async () => {
      try {
        const response = await fetch('/api/trading/markets', { headers: { "X-User-ID": userId } });
        if (response.ok) {
          const data = await response.json();
          setMarkets(data.markets);
          if (data.markets.length > 0) {
            setPair(data.markets[0].symbol);
          }
        }
      } catch (error) {
        console.error('Failed to load markets:', error);
      }
    };
    loadMarkets();
  }, []);

  // Load balance
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await fetch('/api/trading/balance', { headers: { "X-User-ID": userId } });
        if (response.ok) {
          const data = await response.json();
          setBalance(data.usdc_balance || 0);
        }
      } catch (error) {
        console.error('Failed to fetch balance:', error);
      }
    };
    fetchBalance();
  }, [userId]);

  // Fetch escrow balance function
  const fetchEscrowBalance = async () => {
    try {
    console.log("Fetching escrow balance for:", walletAddress);
      const escrowAddress = "0xdF7c107a6d33E73e85b04f97A82634497C1E2Add";
      const userAddress = walletAddress;
      const url = `/api/escrow/balance?escrow_address=${escrowAddress}&user_address=${userAddress}&network=mainnet`;
      const response = await fetch(url);
      console.log("Escrow fetch completed, status:", response.status, "ok:", response.ok);
      if (response.ok) {
        const data = await response.json();
        console.log("Escrow API returned data:", data);
        if (data.success) {
          setEscrowBalance(data.balance_usdc || 0);
        }
      }
    } catch (error) {
      console.log("Escrow fetch error details:", error instanceof Error ? error.message : String(error));
      console.error("Failed to fetch escrow balance:", error);
    }
  };

    console.log("useEffect triggered, walletAddress:", walletAddress);
  useEffect(() => {
    if (walletAddress) {
      fetchEscrowBalance();
    }
  }, [walletAddress]);
  // Load User ID from localStorage
  useEffect(() => {
    const storedUserId = localStorage.getItem("userSessionId");
    if (storedUserId) {
      setUserId(storedUserId);
    }
  }, []);
  // Auto-load saved session when userId is set
  useEffect(() => {
    const fetchSession = async () => {
      if (!userId) return;
      try {
        const response = await fetch("/api/trading/session", {
          headers: { "X-User-ID": userId }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.active && data.session) {
            setPair(data.session.pair);
            setStrategyType(data.session.strategy_type);
            setLeverage(data.session.leverage);
            setTradePct(data.session.trade_size);
            setSessionLoaded(true);
          }
          // Verify trading is actually running
          const statusRes = await api.getTradingStatus(userId);
          setIsTrading(statusRes.active === true);
        }
      } catch (error) {
        console.error("Failed to load session:", error);
      }
    };
    fetchSession();
  }, [userId]);

  // Load price when pair changes
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const symbol = pair.replace('/', '').replace(':USDC', '');
        const response = await fetch(`/api/trading/price/${symbol}`, { headers: { "X-User-ID": userId } });
        if (response.ok) {
          const data = await response.json();
          setPrice(data.price || 0);
        }
      } catch (error) {
        console.error('Failed to fetch price:', error);
      }
    };
    fetchPrice();
  }, [pair]);

  // Update leverage when pair changes
  useEffect(() => {
    const selectedMarket = markets.find(m => m.symbol === pair);
    if (selectedMarket) {
      setMinLeverage(selectedMarket.min_leverage);
      setMaxLeverage(selectedMarket.max_leverage);
      if (!sessionLoaded) setLeverage(selectedMarket.min_leverage);
    }
    if (sessionLoaded) setSessionLoaded(false);
  }, [pair, markets]);

  useEffect(() => {
    const fetchLiveData = async () => {
      if (!isTrading) return;
      try {
        const symbol = pair.replace("/", "-");
        const [posResult, pnlResult] = await Promise.all([
          api.getLivePosition(symbol, userId),
          api.getLivePnl(symbol, userId)
        ]);
        
        // Get 5EMA momentum from trading status
        const statusResult = await api.getTradingStatus(userId);
        
        // Get CHOP indicator
        const chopResult = await api.getChopIndicator(symbol, userId);
        const logs = statusResult.recent_logs || [];
        const emaLog = logs.reverse().find((log: string) => log.includes("5EMA:") && log.includes("momentum="));
        let momentumValue = "Unknown";
        if (emaLog && emaLog.includes("momentum=bullish")) momentumValue = "Bullish";
        if (emaLog && emaLog.includes("momentum=bearish")) momentumValue = "Bearish";
        
        setPosition(posResult);
        setPnl(pnlResult);
        setMomentum(momentumValue);
        setChopData(chopResult);
        
        // Fetch Weekly P&L
        const weeklyRes = await fetch("/api/trading/weekly-pnl/ALL", { headers: { "X-User-ID": userId } });
        if (weeklyRes.ok) {
          const weeklyData = await weeklyRes.json();
          if (weeklyData.success) {
            setWeeklyPnl(weeklyData.data);
          }
        }
      } catch (error) {
        console.error("Failed to fetch live data:", error);
      }
    };

    if (isTrading) {
      fetchLiveData();
      const interval = setInterval(fetchLiveData, 30000);
      return () => clearInterval(interval);
    }
  }, [isTrading, pair, userId]);
  

  const handleStartTrading = async () => {
    try {
      const response = await fetch("/api/trading/start", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-User-ID": userId },
        body: JSON.stringify({
          pair: pair,
          leverage: leverage,
          trade_size: tradePct,
          strategy_type: strategyType
        })
      });
      if (response.ok) {
        setIsTrading(true);
        alert('✅ Trading started successfully!');
      } else {
        const error = await response.json();
        alert('❌ Failed to start trading: ' + error.detail);
      }
    } catch (error) {
      alert('❌ Error: ' + error);
    }
  };

  const handleStopTrading = async () => {
    try {
      const response = await fetch("/api/trading/stop", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-User-ID": userId },
      });
      if (response.ok) {
        setIsTrading(false);
        alert('⏹️ Trading stopped - ready to switch tokens');
      } else {
        const error = await response.json();
        alert('❌ Failed to stop trading: ' + error.detail);
      }
    } catch (error) {
      alert('❌ Error: ' + error);
    }
  };

  return (
    <div className="trading-dashboard">
      <h2>EMA Auto-Trade</h2>
      
      
      {/* Top Section: Trading Pair + Escrow Account */}
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px'}}>
        <div style={{flex: 1}}>
      <div className="form-group">
        <label>Trading Pair ({markets.length} available)</label>
        <select 
          value={pair} 
          onChange={(e) => setPair(e.target.value)}
          className="form-select"
        >
          {markets.map(market => (
            <option key={market.symbol} value={market.symbol}>
              {market.symbol}
            </option>
          ))}
        </select>

      <div className="form-group">
        <label>Strategy Type</label>
        <select 
          value={strategyType} 
          onChange={(e) => setStrategyType(e.target.value)}
          className="form-select"
        >
          <option value="original">Original EMA Strategy</option>
          <option value="tap_confirmation">EMA Tap Confirmation</option>
          <option value="vector">Vector EMA Strategy</option>
          <option value="dtr">DTR Strategy</option>
          <option value="location">STK</option>
        </select>
      </div>
      </div>
      
      <div className="form-group">
        <label>Leverage: {leverage}x (Range: {minLeverage}-{maxLeverage}x)</label>
        <input
          type="range"
          min={minLeverage}
          max={maxLeverage}
          value={leverage}
          onChange={(e) => setLeverage(parseInt(e.target.value))}
        />
      </div>
      
      <div className="form-group">
        <label>Trade Size: {tradePct}% of balance</label>
        <input
          type="range"
          min="1"
          max="100"
          value={tradePct}
          onChange={(e) => setTradePct(parseInt(e.target.value))}
        />
      </div>
      
      <div>Balance: ${balance.toFixed(4)} USDC</div>
      <div>🔌 WebSocket: {websocketConnected ? "CONNECTED" : "DISCONNECTED"}</div>
      {lastUpdated && <div>⏰ Last Updated: {lastUpdated}</div>}
      <div>Price: ${price.toFixed(4)}</div>
        </div>
        
        {/* Escrow Account - Right Side */}
        <div style={{fontSize: '0.9em', textAlign: 'right', minWidth: '250px'}}>
          <h4 style={{margin: '0 0 10px 0', fontSize: '1em'}}>🔒 Escrow Account</h4>
          <div style={{fontSize: '0.85em'}}>Balance: $${escrowBalance.toFixed(2)} USDC (locked) - {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</div>
          <div style={{fontSize: '0.85em'}}>Next Payment: Monday 12am (${weeklyPnl?.profit_share?.toFixed(2) || "0.00"})</div>
          <div style={{fontSize: '0.85em'}}>Status: ${escrowStatus}</div>
          <div style={{marginTop: '8px'}}>
            <button onClick={handleDepositMore} style={{fontSize: '0.8em', marginRight: '5px', padding: '2px 8px'}}>Deposit More</button>
            <button onClick={handleViewHistory} style={{fontSize: '0.8em', padding: '2px 8px'}}>View History</button>
          </div>
        </div>
      </div>
      
      {/* Live Trading Data */}
      {isTrading && (
        <div className="live-trading-section" style={{display: 'flex', justifyContent: 'space-between'}}>
          <div>
            <h3>📊 Live Trading Data</h3>
            {position && (
              <div>💼 Position: {position.size > 0 ? `${position.size.toFixed(2)} ${position.side.toUpperCase()}` : "No Position"}</div>
            )}
            {pnl && position && position.size > 0 && (
              <div>💰 P&L: ${pnl.unrealized_pnl?.toFixed(2) || "0.00"}</div>
            )}
            <div>📈 Momentum: {momentum}</div>
            {chopData && (
              <>
                <div>🔄 CHOP: {chopData.chop_value?.toFixed(1)} ({chopData.market_condition})</div>
                <div>🎯 5/9 Cross: {chopData.blocking_crossovers ? "OFF" : "ON"}</div>
              </>
            )}
          </div>
          <div>
            <h3>📈 Weekly P&L</h3>
            {weeklyPnl ? (
              <>
                <div style={{fontSize: '1.5em', color: weeklyPnl.total_pnl >= 0 ? 'green' : 'red'}}>
                  ${weeklyPnl.total_pnl?.toFixed(2) || "0.00"}
                </div>
                <div>{weeklyPnl.trade_count} trades this week</div>
                  <div>💰 Profit Share: ${weeklyPnl.profit_share?.toFixed(2) || "0.00"}</div>
              </>
            ) : weeklyPnlLoading ? (
              <div>Loading...</div>
            ) : (
              <div>No data</div>
            )}
          </div>
        </div>
      )}
      <div>Calc Amount: {((balance * tradePct / 100) / (price || 1)).toFixed(6)}</div>
      
      <QSMPanel />
      <div className="trading-controls">
        <button 
          className="btn btn-success" 
          onClick={handleStartTrading}
          disabled={isTrading}
        >
          ▶️ {isTrading ? 'Trading Active...' : 'Start Auto-Trade'}
        </button>
        <button 
          className="btn btn-danger" 
          onClick={handleStopTrading}
          disabled={!isTrading}
        >
          ⏹️ Stop Auto-Trade
        </button>
      </div>
    </div>
  );
};

export default CleanTradingDashboard;
